// src/components/TicketVentaPDF.jsx
import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: '#ffffff',
  },
  header: {
    backgroundColor: '#1a202c',
    padding: 20,
    marginBottom: 20,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 10,
    color: '#a0aec0',
  },
  section: {
    marginBottom: 14,
    padding: 14,
    backgroundColor: '#f7fafc',
    borderRadius: 4,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#2d3748',
    marginBottom: 6,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2,
  },
  label: {
    fontSize: 10,
    color: '#718096',
  },
  value: {
    fontSize: 10,
    color: '#2d3748',
    fontWeight: 'normal',
  },
  codeContainer: {
    backgroundColor: '#ebf8ff',
    padding: 10,
    borderRadius: 4,
    alignItems: 'center',
    marginBottom: 10,
  },
  codeText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2b6cb0',
  },
  totalContainer: {
    backgroundColor: '#2d3748',
    padding: 14,
    borderRadius: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 6,
  },
  totalLabel: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  totalValue: {
    fontSize: 22,
    color: '#68d391',
    fontWeight: 'bold',
  },
  footer: {
    marginTop: 16,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 8,
    color: '#a0aec0',
  },
});

const TicketVentaPDF = ({ reserva, servicio, passengerData, cantidadPasajeros }) => {
  // ✅ Validación de datos
  if (!servicio || !reserva) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={{ fontSize: 20, textAlign: 'center', marginTop: 50 }}>
            Error: Datos incompletos para generar el ticket
          </Text>
        </Page>
      </Document>
    );
  }

  const total = servicio.precio * cantidadPasajeros;
  const fechaActual = new Date().toLocaleString();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.headerTitle}>Ticket de Venta</Text>
            <Text style={styles.headerSubtitle}>Venta en Mostrador</Text>
          </View>
        </View>

        {/* Código del ticket */}
        <View style={styles.codeContainer}>
          <Text style={styles.codeText}>{reserva.codigo}</Text>
        </View>

        {/* Datos del viaje */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Datos del viaje</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Origen:</Text>
            <Text style={styles.value}>{servicio.origen || 'No especificado'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Destino:</Text>
            <Text style={styles.value}>{servicio.destino || 'No especificado'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Fecha:</Text>
            <Text style={styles.value}>{servicio.fecha ? new Date(servicio.fecha).toLocaleDateString() : 'No especificada'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Hora de salida:</Text>
            <Text style={styles.value}>{servicio.horaSalida?.substring(0,5) || 'No especificada'}</Text>
          </View>
        </View>

        {/* Datos de los pasajeros */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Pasajeros ({cantidadPasajeros})</Text>
          {passengerData && passengerData.length > 0 ? (
            passengerData.map((pasajero, index) => (
              <View key={index}>
                <View style={styles.row}>
                  <Text style={styles.label}>Pasajero {index + 1}:</Text>
                  <Text style={styles.value}>{pasajero.nombre || 'Sin nombre'} {pasajero.apellido || ''}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Cedula:</Text>
                  <Text style={styles.value}>{pasajero.cedula || 'Sin cedula'}</Text>
                </View>
                {index < passengerData.length - 1 && (
                  <View style={{ height: 4 }} />
                )}
              </View>
            ))
          ) : (
            <Text style={{ fontSize: 10, color: '#718096' }}>No hay pasajeros registrados</Text>
          )}
        </View>

        {/* Total */}
        <View style={styles.totalContainer}>
          <Text style={styles.totalLabel}>TOTAL COBRADO</Text>
          <Text style={styles.totalValue}>${total.toLocaleString()}</Text>
        </View>

        {/* Método de pago */}
        <View style={[styles.section, { marginTop: 10 }]}>
          <View style={styles.row}>
            <Text style={styles.label}>Metodo de pago:</Text>
            <Text style={styles.value}>Efectivo</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Venta realizada en mostrador - Pago en efectivo</Text>
          <Text style={styles.footerText}>Generado el {fechaActual}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default TicketVentaPDF;